import type { JourneyPhase } from '@/types/brand'
import type { RunStageEntryBase } from '@/types/plan'
import { RUN_TIMED_OUT_ERROR_CODE, type SetupRun } from '@/types/setup-run'

export const POST_RUN_STAGES = ['brief', 'write', 'check'] as const

export type PostRunStage = (typeof POST_RUN_STAGES)[number]

export interface BriefCounts {
  readonly material: number
  readonly beats: number
  readonly attempts: number
}

export interface WriteCounts {
  readonly words: number
  readonly units: number
}

export interface CheckCounts {
  readonly hard: number
  readonly criteriaPassed: number
  readonly plain: number
  readonly fixed: boolean
  readonly scoreBefore: number
  readonly scoreAfter: number
}

/** The counts of every stage, by stage. */
export interface PostCountsByStage {
  readonly brief: BriefCounts
  readonly write: WriteCounts
  readonly check: CheckCounts
}

/** One stage of a post run, its counts typed by the stage. */
export type PostRunStageEntry = {
  [S in PostRunStage]: RunStageEntryBase & {
    readonly id: S
    readonly counts: PostCountsByStage[S]
  }
}[PostRunStage]

/** One point of a post's brief: what it says, and the source behind it when it has one. */
export interface PostBeat {
  readonly type: string
  readonly text: string
  readonly quote: string | null
  readonly by: string | null
  readonly who: string | null
  readonly url: string | null
  readonly block: string
}

/** The brief of the post as the screens read it: the claim and what it argues. */
export interface PostBrief {
  readonly beliefSlug: string
  readonly topicId: string | null
  readonly phase: JourneyPhase
  readonly claim: string
  readonly ctaType: string
  readonly format: string
  readonly beats: readonly PostBeat[]
}

export const POST_RUN_ERROR_CODES = [
  'post_run_enqueue_failed',
  'post_run_in_progress',
  'post_run_failed',
  'post_raw_store_failed',
  'post_stage_deadline_exceeded',
  'post_brief_failed',
  'post_no_belief',
  'post_topic_not_linked',
  'belief_map_missing',
  'brand_facts_missing',
  'brand_market_undecided',
  'model_output_invalid',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type PostRunErrorCode = (typeof POST_RUN_ERROR_CODES)[number]

export const POST_RUN_STALE_AFTER_MS = 15 * 60 * 1000

/** One post run, `postRuns/{runId}`; one live run per brand. */
export interface PostRun extends SetupRun<PostRunStage, PostRunStageEntry> {
  readonly errorCode?: PostRunErrorCode
  readonly startedByUserId: string | null
  readonly postId: string | null
  readonly brief: PostBrief | null
  readonly score: number | null
  readonly reasons: readonly string[]
}

/** The review score a post needs before it is ready; under it the post needs a person. */
export const POST_READY_SCORE = 80

/** Whether a checked post needs a person: its score is under the bar. */
export function needsPerson(score: number | null): boolean {
  return score !== null && score < POST_READY_SCORE
}
